// apps/web/src/lib/vendor-display.ts
//
// Vendor-display fallback chain for the Vulnerability Detail surface.
//
// `vulnerabilities.affected_vendors uuid[]` is empty for ~688/8286 CVEs in
// prod even when other vendor signals exist (e.g. CVE-2026-35431 — Microsoft
// Entra ID — has an EMPTY `affected_vendors` array but renders a Microsoft
// Azure supply-chain incident on the same page). The canonical vendor
// projection hangs off the empty UUID column, so the user sees no vendor
// badges despite the data being right there.
//
// This helper resolves a single ordered list of vendors to display by
// consulting tiered signals in preference order:
//
//   1. Canonical vendor namespace (Phase 2 T9 `affectedVendors: VendorRef[]`)
//      — article-derived, uuid-keyed, clickable to
//      `/vulnerabilities?vendor=<id>`. Preferred when populated. Carries
//      `tier` so the renderer can mark Tier-1 vendors with a small "T1" pill.
//      (Phase 2 contract change: replaces the legacy
//      `affectedVendorsResolved: {id,name}[]` field.)
//   2. Supply-chain incidents (`/api/supply-chain/by-cve/:cveId` →
//      `incidents[].providerName`) — distinct provider names, not clickable.
//   3. Vendor advisories (`vuln.vendorAdvisories[].vendor`) — humanised
//      slug labels (msrc → Microsoft, redhat → Red Hat, ...). Not clickable.
//
// The chain is mutually exclusive: once a tier finds vendors the lower tiers
// are not consulted. When Phase 3a populates more `affected_vendors` rows,
// those vendors land in the canonical tier-1 field and supersede the
// fallback transparently — no UI changes required.

import type { Vulnerability, VendorName } from './types'

/**
 * Resolved vendor entry for rendering. `vendorId` is present for Phase B
 * (clickable badge linking to `/vulnerabilities?vendor=<id>`); absent for
 * fallback tiers (supply-chain, advisory, text-extraction) where we render
 * plain-text badges with a small provenance pill.
 *
 * Phase 2 (Plan T11+T12): `tier` is carried through from the canonical
 * `VendorRef.tier` so the renderer can show a Tier-1 pill. `tier` is only
 * meaningful for `source === 'phase-b'` entries (the fallback tiers have
 * no canonical vendor row to derive a tier from); fallback tiers carry
 * `tier: undefined`.
 *
 * Audit item 11 (P2): tier 4 (`text-extraction`) returns `interactive: false`
 * so the renderer can drop the click target — the regex sweep is heuristic
 * and clicking a tier-4 chip would silently not filter the page (no UUID
 * to feed into the `?vendor=<id>` query). Existing call sites can ignore
 * the field; tier-4-aware renderers consult it.
 */
export interface VendorDisplay {
  name: string
  vendorId?: string
  source: 'phase-b' | 'supply-chain' | 'advisory' | 'text-extraction'
  /**
   * Phase 2 vendor unification — numeric vendor tier carried through from
   * `VendorRef.tier`. Only set for `source === 'phase-b'`; `null` for
   * canonical vendors that don't map to a tier (most security_product /
   * other-kind vendors); `undefined` for fallback-tier sources that have
   * no canonical vendor row.
   */
  tier?: number | null
  /**
   * `true` (default behaviour, omitted) for tiers backed by a stable id;
   * `false` for tier 4 where the chip is informational only.
   */
  interactive?: boolean
}

/**
 * Minimal supply-chain incident shape needed by the fallback chain.
 * Mirrors the relevant subset of `IncidentForCve` returned by
 * `GET /api/supply-chain/by-cve/:cveId`.
 */
export interface SupplyChainIncidentForVendorDisplay {
  providerName: string
}

/**
 * Vendor-advisory slug → display label map. Mirrors VENDOR_LABEL in
 * `apps/web/src/components/vulnerabilities/VendorAdvisoryList.tsx`. Kept
 * locally (not imported) so the resolver is self-contained and the test
 * suite does not pull in component code.
 */
const ADVISORY_VENDOR_LABEL: Record<VendorName, string> = {
  msrc: 'Microsoft',
  redhat: 'Red Hat',
  ubuntu: 'Ubuntu',
  cisco: 'Cisco',
  paloalto: 'Palo Alto',
  suse: 'SUSE',
}

/**
 * Dedupe `entries` by case-insensitive `name` while preserving the order of
 * the first occurrence of each name. Used per-tier so callers can pass the
 * raw upstream list without worrying about repeats.
 */
function dedupeByLowercaseName<T extends { name: string }>(entries: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const e of entries) {
    const key = e.name.trim().toLowerCase()
    if (key.length === 0) continue
    if (seen.has(key)) continue
    seen.add(key)
    out.push(e)
  }
  return out
}

/**
 * Tier 4 (P2 audit item 11) — low-confidence text extraction.
 *
 * Only consulted when tiers 1-3 are empty. Sweeps `title + description` for
 * a manually curated known-vendor list and returns the first match as a
 * `VendorDisplay` with `interactive: false`. The B1 vendor-ontology
 * brainstorm (see Phase 1 plans) will replace this with a typed-entity
 * classifier; for now this catches the common case where the title says
 * "Zyxel" (or similar) but Phase B/C enrichment hasn't run yet.
 *
 * Confidence is encoded as `interactive: false` — the chip renders without
 * a click target so users see the signal but no UI promises functionality
 * the tier-4 result can't deliver (no vendor uuid to feed `?vendor=`).
 */
const KNOWN_VENDOR_REGEX_LIST: ReadonlyArray<{
  regex: RegExp
  name: string
}> = [
  { regex: /\bZyxel\b/i, name: 'Zyxel' },
  { regex: /\bFortinet\b/i, name: 'Fortinet' },
  { regex: /\bCheck\s?Point\b/i, name: 'Check Point' },
  { regex: /\bSophos\b/i, name: 'Sophos' },
  { regex: /\bSonicWall\b/i, name: 'SonicWall' },
  { regex: /\bIvanti\b/i, name: 'Ivanti' },
  { regex: /\bBarracuda\b/i, name: 'Barracuda' },
  { regex: /\bF5\b/i, name: 'F5' },
  { regex: /\bCitrix\b/i, name: 'Citrix' },
  { regex: /\bVMware\b/i, name: 'VMware' },
  // Extend as patterns surface; B1 brainstorm produces canonical list.
]

function tier4ExtractFromText(input: {
  title?: string | null
  description?: string | null
}): VendorDisplay[] {
  const haystack = `${input.title ?? ''} ${input.description ?? ''}`
  if (haystack.trim().length === 0) return []
  for (const { regex, name } of KNOWN_VENDOR_REGEX_LIST) {
    if (regex.test(haystack)) {
      return [{ name, source: 'text-extraction', interactive: false }]
    }
  }
  return []
}

/**
 * Resolve the vendors to display for a vulnerability detail surface. See
 * file-level comment for the tier ordering.
 *
 * Returns `[]` when none of the four tiers carry vendor data — the caller
 * then renders nothing (no zero-state stub).
 */
export function resolveVendorsForDisplay(
  vuln: Pick<
    Vulnerability,
    'affectedVendors' | 'vendorAdvisories' | 'title' | 'description'
  >,
  supplyChainIncidents?: SupplyChainIncidentForVendorDisplay[] | null,
): VendorDisplay[] {
  // Tier 1 — canonical vendor namespace (Phase 2 T9 `affectedVendors:
  // VendorRef[]`). Article-validated, uuid-keyed, carries `tier` so the
  // renderer can mark Tier-1 vendors with a small "T1" pill.
  const tier1 = vuln.affectedVendors ?? []
  if (tier1.length > 0) {
    return dedupeByLowercaseName(
      tier1.map((v) => ({
        name: v.name,
        vendorId: v.id,
        source: 'phase-b' as const,
        tier: v.tier,
      })),
    )
  }

  // Tier 2 — supply-chain incident provider names. Distinct providerName
  // values, in arrival order. Plain-text badges with `from supply-chain`
  // provenance pill.
  if (supplyChainIncidents && supplyChainIncidents.length > 0) {
    const tier2 = dedupeByLowercaseName(
      supplyChainIncidents
        .map((i) => i.providerName)
        .filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
        .map((name) => ({ name, source: 'supply-chain' as const })),
    )
    if (tier2.length > 0) return tier2
  }

  // Tier 3 — vendor-advisory feed. Humanise slugs via the local label map;
  // unknown slugs (defensive: future-proofing if the union widens) fall
  // through as the slug itself. Plain-text badges with `from advisory`
  // provenance pill.
  const advisories = vuln.vendorAdvisories ?? []
  if (advisories.length > 0) {
    const labels = advisories
      .map((a) => ADVISORY_VENDOR_LABEL[a.vendor] ?? a.vendor)
      .filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
      .map((name) => ({ name, source: 'advisory' as const }))
    const tier3 = dedupeByLowercaseName(labels)
    if (tier3.length > 0) return tier3
  }

  // Tier 4 (P2 audit item 11) — text extraction from title + description.
  // Only consulted when tiers 1-3 are empty. Returns `interactive: false`
  // so the renderer can suppress the click target on low-confidence chips.
  return tier4ExtractFromText({
    title: vuln.title,
    description: vuln.description,
  })
}
